import type { BeepKind } from './shotClockUtils'

/**
 * Web Audio beeps for the shot clock. iOS 15 Safari only exposes
 * `webkitAudioContext` and keeps the context suspended until a user gesture,
 * so `unlockShotClockAudio()` must be called from a tap / key press handler.
 */

interface BeepSpec {
    frequency: number
    durationMs: number
    gain: number
}

const SPECS: Record<BeepKind, BeepSpec> = {
    loud: { frequency: 1320, durationMs: 220, gain: 1.0 },
    short: { frequency: 880, durationMs: 110, gain: 0.6 },
    long: { frequency: 440, durationMs: 900, gain: 1.0 },
}

let ctx: AudioContext | null = null
let muted = false

function getContext(): AudioContext | null {
    if (ctx) return ctx
    if (typeof window === 'undefined') return null
    const w = window as unknown as { AudioContext?: typeof AudioContext; webkitAudioContext?: typeof AudioContext }
    const Ctor = w.AudioContext || w.webkitAudioContext
    if (!Ctor) return null
    try {
        ctx = new Ctor()
    } catch {
        ctx = null
    }
    return ctx
}

export function unlockShotClockAudio(): void {
    const c = getContext()
    if (c && c.state === 'suspended') {
        c.resume().catch(() => {})
    }
}

export function setShotClockMuted(value: boolean): void {
    muted = value
}

export function isShotClockMuted(): boolean {
    return muted
}

export function playShotClockBeep(kind: BeepKind): void {
    if (muted) return
    const c = getContext()
    if (!c) return
    if (c.state === 'suspended') c.resume().catch(() => {})
    try {
        const spec = SPECS[kind]
        const osc = c.createOscillator()
        const gain = c.createGain()
        const t0 = c.currentTime
        const end = t0 + spec.durationMs / 1000
        osc.type = 'square'
        osc.frequency.value = spec.frequency
        gain.gain.setValueAtTime(0.0001, t0)
        gain.gain.linearRampToValueAtTime(spec.gain * 0.5, t0 + 0.01)
        gain.gain.setValueAtTime(spec.gain * 0.5, end - 0.03)
        gain.gain.linearRampToValueAtTime(0.0001, end)
        osc.connect(gain)
        gain.connect(c.destination)
        osc.start(t0)
        osc.stop(end + 0.02)
    } catch {
        // Audio is best-effort; never break the clock because of it.
    }
}
